import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Card,
  CardActions,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FunctionComponent } from 'react';

import {
  EventUsersQuery,
  FolkloreType,
} from '../../../apollo/__generated__/graphql';
import { Layout } from '../../../navigation/components/Layout/Layout';
import { folkloreNameByFolklore } from '../../../user/services/user.service';
import { HeaderCard } from '../../components/HeaderCard';
import { VisibleUsersType, useEventUsers } from './useEventUsers';

const selectOptions: VisibleUsersType[] = ['ALL', 'INVALID', 'VALID'];

const optionNameByVisibleUsers: Record<VisibleUsersType, string> = {
  ALL: 'Tous',
  INVALID: 'Non validés',
  VALID: 'Validés',
};

const StyledDataGrid = styled(DataGrid<EventUsersQuery['eventUsers'][number]>)(
  ({ theme }) => ({
    '& .invalid': {
      backgroundColor: theme.palette.contrastThreshold,
      color: theme.palette.text.secondary,
    },
    '& .valid': {
      backgroundColor: theme.palette.background.default,
    },
  }),
);

export const EventUsers: FunctionComponent = () => {
  const {
    currentEvent,
    eventUsers,
    loading,
    visibleUsers,
    onVisibleUsersChange,
    clearSearch,
    onSearchChange,
    onSortModelChange,
    sortModel,
    search,
  } = useEventUsers();

  const columns: GridColDef<EventUsersQuery['eventUsers'][number]>[] = [
    {
      field: 'firstname',
      headerName: 'Nom',
      width: 150,
      renderCell: (params) =>
        `${params.row.firstname} ${params.row.lastname?.substring(0, 1)}.`,
    },
    {
      field: 'nickname',
      headerName: 'Surnom',
      width: 150,
    },
    {
      field: 'folklore',
      headerName: 'Folklore',
      renderCell: (params) =>
        folkloreNameByFolklore[params.row.folklore ?? FolkloreType.Other],
      width: 100,
    },
    {
      field: 'studyTown',
      headerName: 'Ville',
      width: 150,
    },
    {
      field: 'studies',
      headerName: 'Fillière',
      width: 150,
    },
  ];

  return (
    <Layout title="Participants" activeTab="eventUsers">
      {currentEvent ? (
        <>
          <HeaderCard
            title={`Participants du ${currentEvent.name}`}
            description="Parcourez les inscrits et filtrez la liste selon leur statut de validation."
          />
          <Card sx={{ mb: 2 }}>
            <CardActions>
              <Select
                value={visibleUsers}
                onChange={onVisibleUsersChange}
                size="small"
              >
                {selectOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {optionNameByVisibleUsers[option]}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label="Rechercher un participant"
                size="small"
                value={search}
                onChange={onSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon />,
                  endAdornment:
                    search.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={clearSearch}
                          onMouseDown={clearSearch}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                }}
              />
            </CardActions>
          </Card>
          <StyledDataGrid
            rows={eventUsers}
            columns={columns}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            loading={loading}
            getRowClassName={(params) =>
              params.row.isValidated ? 'valid' : `invalid`
            }
          />
        </>
      ) : (
        <HeaderCard />
      )}
    </Layout>
  );
};
